// Season Dashboard — full replacement for the old 4-tab StatsView.
// One scrolling, story-first page: header → story tiles → winning formula →
// leaders → players → hitter truth → pitching command center → deep intel →
// fairness. Gating (Elite / head-coach / styleOfPlay) is enforced HERE, in
// one place. StatsView.tsx re-exports this component — the App.tsx and
// SeasonHistoryModal mount contracts are unchanged.

import React, { useState, useEffect, useMemo } from 'react';
import { AnimatePresence } from 'motion/react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { BarChart2 } from 'lucide-react';
import { GS } from '../../lib/designTokens';
import { PITCH_COUNT_RULESETS } from '../../lib/pitch-count-rules';
import { buildCoachsEyePayload } from '../../lib/coachs-eye-payload';
import { buildFieldTimeReport } from '../../lib/field-time-report';
import { FieldTimeReportModal } from '../FieldTimeReport/FieldTimeReportModal';
import { CoachsEyeModal } from '../CoachsEyeModal';
import { useSeasonStats } from './useSeasonStats';
import { SeasonHeader } from './SeasonHeader';
import { StoryTiles } from './StoryTiles';
import { WinningFormulaCard } from './WinningFormulaCard';
import { LeadersRow } from './LeadersRow';
import { PlayerGrid } from './PlayerGrid';
import { PlayerDetailSheet } from './PlayerDetailSheet';
import { DueListCard } from './DueListCard';
import { PitchingCommandCenter, AvailableToPitchCard } from './PitchingCommandCenter';
import { DeepIntelSection } from './DeepIntelSection';
import { FairnessSection, GameBallsCard } from './FairnessSection';
import { StatsTableSheet, TableKind } from './StatsTableSheet';
import { EliteLockCard, LockPreview } from './EliteLockCard';
import { SectionHeader, FadeIn } from './ui';

/**
 * One completed game in the archived-season schedule. Deliberately read only:
 * no swipe-to-delete, no start/resume, no edit. A past season is a record, not
 * a workspace, so this is its own small row rather than a locked-down GameCard.
 */
const PastGameRow = ({ game }: { game: any }) => {
  const date = game.date ? new Date(game.date) : null;
  const dateLabel = date && !isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';
  const outcome = game.result?.outcome;

  return (
    <div
      className="flex items-center gap-3 rounded-control border px-3 py-2.5"
      style={{ borderColor: GS.line, backgroundColor: GS.panel }}
    >
      <span className="text-[11px] font-semibold tabular-nums w-14 shrink-0" style={{ color: GS.ink2 }}>
        {dateLabel}
      </span>
      <span className="text-[13px] font-semibold flex-1 truncate" style={{ color: GS.ink }}>
        {game.opponent || 'Opponent'}
      </span>
      {game.result && (
        <span
          className="text-[13px] font-black tabular-nums shrink-0"
          style={{ color: outcome === 'L' ? GS.redInk : GS.ink }}
        >
          {outcome} {game.result.myScore}–{game.result.opponentScore}
        </span>
      )}
    </div>
  );
};

export const SeasonDashboard = ({ team, players, isElite, isHeadCoach, onUpgrade, autoOpenSmartScout, onSmartScoutAutoOpened, viewSeason }: {
  team: any;
  players: any[];
  isElite?: boolean;
  isHeadCoach?: boolean;
  onUpgrade?: () => void;
  autoOpenSmartScout?: boolean;
  onSmartScoutAutoOpened?: () => void;
  viewSeason?: { season: string; year: number } | null;
}) => {
  const data = useSeasonStats(team, players, viewSeason);
  const isHistoryMode = !!viewSeason;
  const elite = !!isElite || team?.isDemoSession === true;
  const playersPitch = (team?.settings?.styleOfPlay || team?.styleOfPlay) === 'playerspitch';

  const [showCoachsEye, setShowCoachsEye] = useState(false);
  const [openPlayerId, setOpenPlayerId] = useState<string | null>(null);
  const [tableKind, setTableKind] = useState<TableKind>(null);


  const playersById = useMemo(
    () => Object.fromEntries(players.map((p: any) => [p.id, p])),
    [players],
  );

  // Field Time Report — the printable, parent-facing playing-time record. It was
  // already built (src/lib/field-time-report.ts) but reachable only from an
  // unlabelled share icon inside the player-edit form, so the artifact that
  // answers "why didn't my kid play?" was three taps deep and unnamed.
  const [reportPlayerId, setReportPlayerId] = useState<string | null>(null);
  const reportData = useMemo(() => {
    if (!reportPlayerId || !team) return null;
    const player = playersById[reportPlayerId];
    return player ? buildFieldTimeReport(player, team, data.games) : null;
  }, [reportPlayerId, team, playersById, data.games]);

  // League pitch-cap rules for the innings forecast (TournamentView pattern)
  const capRules = useMemo(() => {
    const ruleset = PITCH_COUNT_RULESETS[team?.settings?.tournamentOrg || team?.settings?.pitchCountRuleset || 'little_league'];
    return ruleset?.ageGroups?.[team?.ageGroup] ?? null;
  }, [team?.settings?.tournamentOrg, team?.settings?.pitchCountRuleset, team?.ageGroup]);

  useEffect(() => {
    if (autoOpenSmartScout && elite) {
      setShowCoachsEye(true);
      onSmartScoutAutoOpened?.();
    }
  }, [autoOpenSmartScout]);

  const coachsEyePayload = useMemo(
    () => buildCoachsEyePayload({
      team, players, games: data.games, stats: data.stats,
      streakSignals: data.streakSignals, teamPitching: data.teamPitching,
    }),
    [team, players, data],
  );

  if (data.loading) {
    return (
      <div className="p-6 text-center text-[10px] font-bold uppercase" style={{ color: GS.ink3, letterSpacing: '0.2em' }}>
        Loading...
      </div>
    );
  }

  const noData = data.sortedGames.length === 0 && !data.hasGameStats;
  const tableAction = (kind: Exclude<TableKind, null>, lockedFree?: boolean) => (
    <button
      onClick={() => (lockedFree && !elite) ? onUpgrade?.() : setTableKind(kind)}
      className="text-[10px] font-black uppercase"
      style={{ color: GS.ink2, letterSpacing: '0.08em' }}
    >
      Full table ›
    </button>
  );

  return (
    <div style={{ background: GS.bg, minHeight: '100%' }}>
      <AnimatePresence>
        {showCoachsEye && (
          <CoachsEyeModal
            payload={coachsEyePayload}
            onClose={() => setShowCoachsEye(false)}
            onApplyAttributes={async (playerId: string, mergedAttributes: any) => {
              await updateDoc(doc(db, `teams/${team.id}/players/${playerId}`), { attributes: mergedAttributes });
            }}
          />
        )}
      </AnimatePresence>

      <div className="px-4 pb-10 pt-4">
        <SeasonHeader
          data={data}
          elite={elite}
          isHistoryMode={isHistoryMode}
          viewSeason={viewSeason}
          playersPitch={playersPitch}
          onOpenScout={() => setShowCoachsEye(true)}
        />

        {noData ? (
          <div className="mt-6 rounded-3xl border-2 border-dashed p-10 text-center" style={{ borderColor: GS.line }}>
            <BarChart2 className="w-10 h-10 mx-auto" style={{ color: GS.ink3 }} />
            <p className="text-sm font-black mt-3" style={{ color: GS.ink }}>No stats yet</p>
            <p className="text-[11px] font-semibold mt-1 leading-relaxed" style={{ color: GS.ink2 }}>
              {isHeadCoach ? 'Open a game and import its GameChanger CSV, or complete a game in Dugout. The story starts with the first box score.' : 'Stats appear once games are logged or imported.'}
            </p>
          </div>
        ) : (
          <>
            {/* ── The Story ── */}
            <SectionHeader id="sec-story" title="The Story" />
            {elite ? (
              <div className="space-y-3">
                <FadeIn><StoryTiles data={data} /></FadeIn>
                <SectionHeader title="Winning Formula" />
                <FadeIn><WinningFormulaCard formula={data.winningFormula} /></FadeIn>
                <SectionHeader title="Leaders" />
                <FadeIn><LeadersRow data={data} /></FadeIn>
              </div>
            ) : (
              <EliteLockCard title="The story behind your stats: quality at-bats, free bases, clutch, your winning formula" onUpgrade={onUpgrade}>
                <LockPreview />
              </EliteLockCard>
            )}

            {/* ── Players ── */}
            <SectionHeader
              id="sec-players"
              title="Players"
              action={elite ? tableAction('batting') : undefined}
            />
            <FadeIn><PlayerGrid data={data} elite={elite} onOpenPlayer={setOpenPlayerId} onUpgrade={onUpgrade} /></FadeIn>
            {elite && (
              <>
                <SectionHeader title="Hitter Truth" />
                <FadeIn><DueListCard truth={data.hitterTruth} /></FadeIn>
              </>
            )}

            {/* ── Pitching ── */}
            {playersPitch && (
              <>
                <SectionHeader
                  id="sec-pitching"
                  title="Pitching · Command Center"
                  action={elite ? tableAction('pitching') : undefined}
                />
                {/* Available to Pitch is FREE — it's a safety feature */}
                <div className="space-y-3">
                  <AvailableToPitchCard data={data} />
                  {elite ? (
                    <PitchingCommandCenter data={data} capRules={capRules} isHeadCoach={isHeadCoach} />
                  ) : (
                    <EliteLockCard title="Command scores, innings forecasts & the walks that are costing you runs" onUpgrade={onUpgrade}>
                      <LockPreview />
                    </EliteLockCard>
                  )}
                </div>

                {elite && (
                  <>
                    <SectionHeader title="Deep Intel · From Your Scorebook" />
                    <DeepIntelSection team={team} data={data} playersById={playersById} playersPitch={playersPitch} />
                  </>
                )}
              </>
            )}
            {!playersPitch && elite && (
              <>
                <SectionHeader title="Deep Intel · From Your Scorebook" />
                <DeepIntelSection team={team} data={data} playersById={playersById} playersPitch={playersPitch} />
              </>
            )}

            {/* ── Fairness ── */}
            <SectionHeader
              id="sec-fairness"
              title="Fairness · Field Time"
              sub={`${data.totalGamesWithLogs} ${data.totalGamesWithLogs === 1 ? 'game' : 'games'} logged`}
            />
            <FadeIn>
              <div className="space-y-3">
                <FairnessSection
                  data={data}
                  onOpenReport={isHistoryMode ? undefined : setReportPlayerId}
                />
                <GameBallsCard team={team} players={players} viewSeason={viewSeason} />
              </div>
            </FadeIn>

            {/* ── Schedule (history only) ──
                The archived season's games, read only. No start/resume/prepare
                affordances: a past season can never launch a live session. */}
            {isHistoryMode && data.sortedGames.length > 0 && (
              <>
                <SectionHeader
                  id="sec-schedule"
                  title="Schedule"
                  sub={`${data.sortedGames.length} ${data.sortedGames.length === 1 ? 'game' : 'games'}`}
                />
                <FadeIn>
                  <div className="space-y-2">
                    {data.sortedGames.map((g: any) => (
                      <PastGameRow key={g.id} game={g} />
                    ))}
                  </div>
                </FadeIn>
              </>
            )}
          </>
        )}
      </div>

      {/* Sheets */}
      <PlayerDetailSheet
        playerId={openPlayerId}
        data={data}
        capRules={capRules}
        isHeadCoach={isHeadCoach}
        onClose={() => setOpenPlayerId(null)}
      />
      <StatsTableSheet kind={tableKind} data={data} onClose={() => setTableKind(null)} />
      {reportData && (
        <FieldTimeReportModal data={reportData} onClose={() => setReportPlayerId(null)} />
      )}
    </div>
  );
};
